import "./Settings.scss";
import { useState, memo, useEffect } from "react";
import { useMediaQuery } from "@mui/material";
import GreenButton from "../../components/GreenButton/GreenButton";
import GeneralSettings from "./GeneralSettings/GeneralSettings";
import Terms from "./Terms/Terms";
import Links from "./Links/Links";
import { usePermissions } from "../../hooks/usePermissions";
import AreaSelector from "./Areas/AreaSelector";
import AreasAnalytics from "./AreasAnalytics/AreasAnalytics.jsx";

// Navigation configuration with permissions
const SETTINGS_NAVIGATION = [
  {
    id: "GeneralSettings",
    text: "اعدادات عامة",
    component: GeneralSettings,
    viewPermission: 94, // رؤية الاعدادات العامة
  },
  {
    id: "Areas",
    text: "المناطق والمدن",
    component: AreaSelector,
    viewPermission: 132, // رؤية الاعدادات العامة
  },
  {
    id: "Terms",
    text: "الشروط والاحكام",
    component: Terms,
    viewPermission: 99, // رؤية كل الشروط
  },
  {
    id: "Links",
    text: "روابط تواصل معنا",
    component: Links,
    viewPermission: 97, // رؤية روابط تواصل معانا
  },
  {
    id: "AreasAnalytics",
    text: "تحليلات المناطق ",
    component: AreasAnalytics,
    viewPermission: 135, // رؤية روابط تواصل معانا
  },
];

// Navigation Buttons Component
const NavigationButtons = memo(
  ({ currentView, onViewChange, isMobile, hasPermission }) => {
    // Filter navigation items based on permissions
    const allowedNavigation = SETTINGS_NAVIGATION.filter((item) =>
      hasPermission(item.viewPermission)
    );

    if (allowedNavigation.length === 0) {
      return null;
    }

    return (
      <div className="btns-container">
        {allowedNavigation.map(({ id, text }) => (
          <GreenButton
            key={id}
            text={text}
            size={isMobile ? "full" : "medium"}
            variant={currentView === id ? "fill" : "outlined"}
            onClick={() => onViewChange(id)}
          />
        ))}
      </div>
    );
  }
);

NavigationButtons.displayName = "NavigationButtons";

// Header Component
const SettingsHeader = memo(
  ({ currentView, onViewChange, isMobile, hasPermission }) => (
    <div className="special-header">
      <p className="title">اعدادات النظام</p>
      <NavigationButtons
        currentView={currentView}
        onViewChange={onViewChange}
        isMobile={isMobile}
        hasPermission={hasPermission}
      />
    </div>
  )
);

SettingsHeader.displayName = "SettingsHeader";

// Content Component
const SettingsContent = memo(({ currentView, hasPermission }) => {
  const currentNavItem = SETTINGS_NAVIGATION.find(
    (item) => item.id === currentView
  );

  // Check if user has permission to view current content
  if (!currentNavItem || !hasPermission(currentNavItem.viewPermission)) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "300px",
          backgroundColor: "white",
          borderRadius: "8px",
          margin: "30px",
        }}
      >
        <p
          style={{
            fontSize: "18px",
            color: "#666",
            textAlign: "center",
          }}
        >
          ليس لديك صلاحية لعرض هذا القسم
        </p>
      </div>
    );
  }

  const CurrentViewComponent = currentNavItem.component || (() => null);

  return (
    <div className="" style={{ padding: "30px" }}>
      <CurrentViewComponent />
    </div>
  );
});

SettingsContent.displayName = "SettingsContent";

// Main Settings Component
const Settings = () => {
  const { hasPermission } = usePermissions();
  const isMobile = useMediaQuery("(max-width:767px)");
  const [currentView, setCurrentView] = useState("");

  // Set initial view based on permissions
  useEffect(() => {
    const allowedNavigation = SETTINGS_NAVIGATION.filter((item) =>
      hasPermission(item.viewPermission)
    );

    if (allowedNavigation.length > 0 && !currentView) {
      setCurrentView(allowedNavigation[0].id);
    }
  }, [hasPermission, currentView]);

  // Check if user has any settings permissions
  const hasAnySettingsPermission = SETTINGS_NAVIGATION.some((item) =>
    hasPermission(item.viewPermission)
  );

  // If no permissions, show restricted access
  if (!hasAnySettingsPermission) {
    return (
      <div className="settings">
        <div className="special-header">
          <p className="title">اعدادات النظام</p>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
            backgroundColor: "white",
            borderRadius: "8px",
            margin: "30px",
          }}
        >
          <p
            style={{
              fontSize: "20px",
              color: "#666",
              textAlign: "center",
            }}
          >
            ليس لديك صلاحية للوصول إلى إعدادات النظام
          </p>
        </div>
      </div>
    );
  }

  const handleViewChange = (viewId) => {
    const navItem = SETTINGS_NAVIGATION.find((item) => item.id === viewId);
    if (navItem && hasPermission(navItem.viewPermission)) {
      setCurrentView(viewId);
    }
  };

  return (
    <div className="settings">
      <SettingsHeader
        currentView={currentView}
        onViewChange={handleViewChange}
        isMobile={isMobile}
        hasPermission={hasPermission}
      />
      <SettingsContent
        currentView={currentView}
        hasPermission={hasPermission}
      />
    </div>
  );
};

export default Settings;
